// deps
import React, {
    ReactElement,
    KeyboardEvent,
    useState,
    FocusEventHandler,
    KeyboardEventHandler,
    ChangeEventHandler,
    useCallback,
    forwardRef,
    useRef,
    useImperativeHandle,
} from "react";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import {
    InputFieldContainer,
    InputWrapper,
    StyledInput,
    Label,
    ErrorContainer,
    WarningContainer,
    ErrorIconSvg,
} from "./styles/InputField.v4.styled";

// hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

// interfaces
import { InputFieldAttributes } from "@core-ui/interfaces/Input";

export interface InputFieldProps {
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    value?: string;
    onBlur?: (name?: string) => void;
    onChange?: (value: string, name?: string) => void;
    onUp?: (event: KeyboardEvent<HTMLInputElement>, name?: string) => void;
    onDown?: (event: KeyboardEvent<HTMLInputElement>, name?: string) => void;
    onFocus?: (name?: string) => void;
    attrs?: InputFieldAttributes;
    error?: boolean;
    inline?: boolean;
    style?: object;
    label?: string;
    errorMessage?: string;
    setSelectionRange?: boolean;
    showFocusBar?: boolean;
    disabled?: boolean;
    keepLabelActive?: boolean;
    warningMessage?: string;
    warning?: boolean;
}

interface InputFieldRefAttribute {
    focus: () => void;
    blur: () => void;
}

const ErrorIcon = React.memo(() => (
    <ErrorIconSvg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#E53554" />
        <path d="M10 5L10 10" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="8.5" y="13" width="3" height="3" rx="1.5" fill="white" />
    </ErrorIconSvg>
));

function Input(
    { attrs = {}, ...props }: InputFieldProps,
    ref:
        | ((instance: InputFieldRefAttribute | null) => void)
        | React.MutableRefObject<InputFieldRefAttribute | null>
        | null,
): ReactElement {
    const { errorMessage, showFocusBar, warningMessage } = props;
    const [isLabelActive, setLabelActive] = useState(props.keepLabelActive ?? false);

    const _input = useRef<HTMLInputElement>(null);
    const _inputContainer = useRef<HTMLDivElement>(null);
    const _labelRef = useRef<HTMLLabelElement>(null);

    const getInput = useCallback((): HTMLInputElement | null => _input.current, [_input]);
    const _isLabelActive = useCallback((): boolean => {
        const $input = getInput();
        return (
            !isEmpty(props.value) ||
            !isEmpty(attrs.placeholder) ||
            ($input !== null && ($input === document.activeElement || !isEmpty($input.value)))
        );
    }, [attrs.placeholder, getInput, props.value]);

    const getInputName = useCallback((): string | undefined => {
        return attrs.name;
    }, [attrs.name]);

    const getInputId = useCallback((): string | undefined => {
        return attrs.id || attrs.name;
    }, [attrs.id, attrs.name]);

    const onInputFocus = useCallback((): void => {
        setLabelActive(_isLabelActive());
        if (props.onFocus !== undefined) {
            props.onFocus(getInputName());
        }
    }, [_isLabelActive, getInputName, props]);

    const onInputBlur: FocusEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (event.target !== getInput()) {
                return;
            }

            setLabelActive(_isLabelActive());
            if (typeof props.onBlur === "function") {
                props.onBlur(getInputName());
            }
        },
        [_isLabelActive, getInput, getInputName, props],
    );

    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (typeof props.onUp === "function") {
                props.onUp(event, getInputName());
            }
        },
        [getInputName, props],
    );

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (typeof props.onDown === "function") {
                props.onDown(event, getInputName());
            }
        },
        [getInputName, props],
    );

    const onInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (typeof props.onChange === "function") {
                props.onChange(event.target.value, getInputName());
            }
        },
        [getInputName, props],
    );

    useImperativeHandle(ref, () => ({
        focus: (): void => {
            _input.current?.setAttribute("autofocus", "autofocus");
            _input.current?.focus();
        },
        blur: (): void => {
            _input.current?.blur();
        },
    }));

    useEffectOnce(() => {
        if (_input.current === document.activeElement) {
            setLabelActive(_isLabelActive());
            if (props.onFocus !== undefined) {
                props.onFocus(getInputName());
            }
        }
    });

    return (
        <InputFieldContainer
            $inline={props.inline}
            className={props.className}
            style={props.style}
            ref={_inputContainer}
        >
            <InputWrapper>
                <StyledInput
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    {...attrs}
                    ref={_input}
                    className={props.inputClassName}
                    $showFocusBar={showFocusBar}
                    $error={props.error}
                    $warning={props.warning}
                    $disabled={props.disabled}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                    onChange={onInputChange}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    value={props.value}
                    id={getInputId()}
                    disabled={props.disabled ?? false}
                />
                {!isEmpty(errorMessage) && !showFocusBar && <ErrorIcon />}
                <Label
                    className={props.labelClassName}
                    $isActive={isLabelActive || props.keepLabelActive}
                    $error={props.error}
                    $warning={props.warning}
                    $disabled={props.disabled}
                    htmlFor={getInputId()}
                    ref={_labelRef}
                >
                    {props.label}
                </Label>
            </InputWrapper>
            {!isEmpty(errorMessage) && (
                <ErrorContainer $showFocusBar={showFocusBar}>
                    <Text variant="V2.body3.neutral.medium" color="negative">
                        {errorMessage}
                    </Text>
                </ErrorContainer>
            )}
            {!isEmpty(warningMessage) && isEmpty(errorMessage) && (
                <WarningContainer $showFocusBar={showFocusBar}>
                    <Text variant="V2.body3.neutral.medium" color="warning">
                        {warningMessage}
                    </Text>
                </WarningContainer>
            )}
        </InputFieldContainer>
    );
}

export default forwardRef<InputFieldRefAttribute, InputFieldProps>(Input);
